/**
 * Image with text — scoped autoplay, reduced-motion, viewport pause,
 * and HYLA custom video controls (Shopify-hosted only).
 * Loaded only when a video source is selected. Does not change global media players.
 */
(function () {
  if (customElements.get('iwt-video')) return;

  class IwtVideo extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.playback = this.dataset.playback || 'play_button';
      this.source = this.dataset.source || 'shopify';
      this.player = this.querySelector('.iwt-video__player') || this.querySelector('.iwt-video__media-frame > video');
      this.externalHost = this.querySelector('[data-iwt-external-host]');
      this.reducedPlay = this.querySelector('[data-iwt-reduced-play]');
      this.deferred = this.querySelector('deferred-media');
      this.controlsEl = this.querySelector('[data-iwt-controls]');
      this.muteBtn = this.controlsEl ? this.controlsEl.querySelector('[data-iwt-mute]') : null;
      this.toggleBtn = this.controlsEl ? this.controlsEl.querySelector('[data-iwt-toggle]') : null;
      this.userPaused = false;
      this.pausingFromObserver = false;
      this.playbackEnded = false;
      this.isIntersecting = false;
      this.mediaListenersAttached = false;
      this.reducedPlayBound = false;
      this.controlsBound = false;
      this.deferredClickBound = false;
      this.reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.labelPlay = (this.controlsEl && this.controlsEl.dataset.labelPlay) || 'Play video';
      this.labelPause = (this.controlsEl && this.controlsEl.dataset.labelPause) || 'Pause video';
      this.labelSoundOn = (this.controlsEl && this.controlsEl.dataset.labelSoundOn) || 'Turn sound on';
      this.labelSoundOff = (this.controlsEl && this.controlsEl.dataset.labelSoundOff) || 'Turn sound off';

      this.onReducedChange = this.handleReducedChange.bind(this);
      this.onIntersect = this.handleIntersect.bind(this);
      this.onPlay = this.handlePlay.bind(this);
      this.onPause = this.handlePause.bind(this);
      this.onEnded = this.handleEnded.bind(this);
      this.onVolumeChange = this.handleVolumeChange.bind(this);
      this.onReducedPlay = this.handleReducedPlay.bind(this);
      this.onSectionUnload = this.handleSectionUnload.bind(this);
      this.onCanPlay = this.handleCanPlay.bind(this);
      this.onMediaError = this.handleMediaError.bind(this);
      this.onControlsClick = this.handleControlsClick.bind(this);
      this.onDeferredClick = this.handleDeferredClick.bind(this);

      if (typeof this.reducedQuery.addEventListener === 'function') {
        this.reducedQuery.addEventListener('change', this.onReducedChange);
      } else if (typeof this.reducedQuery.addListener === 'function') {
        this.reducedQuery.addListener(this.onReducedChange);
      }

      document.addEventListener('shopify:section:unload', this.onSectionUnload);
      this.bindControls();
      this.watchDeferredMedia();

      if (this.playback === 'autoplay') {
        this.setupAutoplay();
      } else if (this.source === 'shopify') {
        this.bindHostedPlayer();
      }
    }

    disconnectedCallback() {
      this.cleanup();
    }

    prefersReducedMotion() {
      return this.reducedQuery.matches;
    }

    bindControls() {
      if (!this.controlsEl || this.controlsBound) return;
      this.controlsEl.addEventListener('click', this.onControlsClick);
      this.controlsBound = true;
    }

    handleControlsClick(event) {
      const muteBtn = event.target.closest('[data-iwt-mute]');
      const toggleBtn = event.target.closest('[data-iwt-toggle]');
      if (!muteBtn && !toggleBtn) return;
      event.preventDefault();
      event.stopPropagation();
      if (muteBtn) this.toggleMute();
      if (toggleBtn) this.togglePlayback();
    }

    watchDeferredMedia() {
      if (!this.deferred) return;
      if (!this.deferredClickBound) {
        this.deferred.addEventListener('click', this.onDeferredClick);
        this.deferredClickBound = true;
      }
      if (this.deferredObserver || typeof MutationObserver === 'undefined') return;

      this.deferredObserver = new MutationObserver(() => {
        this.bindHostedPlayer();
      });
      this.deferredObserver.observe(this.deferred, {
        attributes: true,
        attributeFilter: ['loaded'],
        childList: true,
        subtree: true
      });
    }

    handleDeferredClick() {
      window.setTimeout(() => this.bindHostedPlayer(), 0);
    }

    bindHostedPlayer() {
      if (this.source !== 'shopify') return;
      const video =
        this.querySelector('.iwt-video__player') ||
        this.querySelector('.iwt-video__media-frame video') ||
        this.querySelector('video');
      if (!video) return;

      const isNewPlayer = video !== this.player;
      this.player = video;
      this.prepareHostedPlayer();
      if (isNewPlayer || !this.mediaListenersAttached) {
        this.attachMediaListeners();
      }

      const deferredReady = !this.deferred || this.deferred.hasAttribute('loaded');
      if (this.playback === 'play_button' && deferredReady) {
        this.showCustomControls();
      }
      this.syncControls();
    }

    prepareHostedPlayer() {
      if (!this.player) return;
      this.player.controls = false;
      this.player.removeAttribute('controls');
      this.player.setAttribute('playsinline', '');
      this.player.setAttribute('webkit-playsinline', '');
      this.player.playsInline = true;
    }

    attachMediaListeners() {
      if (!this.player) return;
      this.detachMediaListeners();
      this.player.addEventListener('play', this.onPlay);
      this.player.addEventListener('pause', this.onPause);
      this.player.addEventListener('ended', this.onEnded);
      this.player.addEventListener('volumechange', this.onVolumeChange);
      this.player.addEventListener('canplay', this.onCanPlay);
      this.player.addEventListener('loadeddata', this.onCanPlay);
      this.player.addEventListener('error', this.onMediaError);
      this.mediaListenersAttached = true;
    }

    detachMediaListeners() {
      if (!this.player || !this.mediaListenersAttached) return;
      this.player.removeEventListener('play', this.onPlay);
      this.player.removeEventListener('pause', this.onPause);
      this.player.removeEventListener('ended', this.onEnded);
      this.player.removeEventListener('volumechange', this.onVolumeChange);
      this.player.removeEventListener('canplay', this.onCanPlay);
      this.player.removeEventListener('loadeddata', this.onCanPlay);
      this.player.removeEventListener('error', this.onMediaError);
      this.mediaListenersAttached = false;
    }

    setupAutoplay() {
      if (this.prefersReducedMotion()) {
        this.enableReducedMotionMode();
        return;
      }

      this.hideReducedPlay();

      if (this.source === 'shopify' && this.player) {
        this.player.muted = true;
        this.prepareHostedPlayer();
        this.attachMediaListeners();
        if (this.player.readyState >= 2) {
          this.tryPlay();
          this.showCustomControls();
        }
        this.observe();
      } else if (this.source === 'external') {
        this.mountExternalAutoplay();
      }
    }

    mountExternalAutoplay() {
      if (!this.externalHost || this.externalHost.querySelector('video-section')) return;
      const template = this.externalHost.querySelector('template');
      if (!template) return;
      const node = template.content.firstElementChild.cloneNode(true);
      this.externalHost.appendChild(node);
    }

    enableReducedMotionMode() {
      this.classList.add('is-reduced-motion');
      this.hideCustomControls();
      if (this.player) {
        this.player.pause();
        this.player.removeAttribute('autoplay');
      }
      this.showReducedPlay();
    }

    showReducedPlay() {
      if (!this.reducedPlay) return;
      this.reducedPlay.hidden = false;
      if (!this.reducedPlayBound) {
        this.reducedPlay.addEventListener('click', this.onReducedPlay);
        this.reducedPlayBound = true;
      }
    }

    hideReducedPlay() {
      if (!this.reducedPlay) return;
      this.reducedPlay.hidden = true;
      if (this.reducedPlayBound) {
        this.reducedPlay.removeEventListener('click', this.onReducedPlay);
        this.reducedPlayBound = false;
      }
    }

    handleReducedPlay(event) {
      event.preventDefault();
      this.classList.remove('is-reduced-motion');
      this.hideReducedPlay();
      this.userPaused = false;
      this.playbackEnded = false;

      if (this.source === 'shopify' && this.player) {
        this.prepareHostedPlayer();
        this.attachMediaListeners();
        this.player.muted = false;
        this.tryPlay();
        this.showCustomControls();
        this.observe();
        return;
      }

      if (this.source === 'external') {
        this.mountExternalAutoplay();
      }
    }

    handleReducedChange() {
      if (this.playback !== 'autoplay') return;
      if (this.prefersReducedMotion()) {
        this.disconnectObserver();
        this.enableReducedMotionMode();
      } else {
        this.classList.remove('is-reduced-motion');
        this.hideReducedPlay();
        this.setupAutoplay();
      }
    }

    observe() {
      if (this.observer || typeof IntersectionObserver === 'undefined') return;
      this.observer = new IntersectionObserver(this.onIntersect, {
        threshold: 0.25,
        rootMargin: '0px 0px 40px 0px'
      });
      this.observer.observe(this);
    }

    disconnectObserver() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        this.isIntersecting = entry.isIntersecting;
        if (!this.player || this.prefersReducedMotion()) return;
        if (entry.isIntersecting) {
          if (!this.userPaused && !this.playbackEnded) this.tryPlay();
        } else {
          this.pausingFromObserver = true;
          this.player.pause();
          this.pausingFromObserver = false;
        }
      });
    }

    handleCanPlay() {
      this.prepareHostedPlayer();
      this.syncControls();
      if (this.playback === 'autoplay' && !this.prefersReducedMotion() && !this.classList.contains('is-reduced-motion')) {
        this.showCustomControls();
        if (this.userPaused) return;
        if (this.observer && !this.isIntersecting) return;
        this.tryPlay();
      }
    }

    handleMediaError() {
      this.enableReducedMotionMode();
    }

    tryPlay() {
      if (!this.player) return;
      const playAttempt = this.player.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch((error) => {
          if (!this.isConnected || this.userPaused) return;
          if (error && error.name === 'AbortError') return;
          this.enableReducedMotionMode();
        });
      }
    }

    handlePlay() {
      this.userPaused = false;
      this.playbackEnded = false;
      this.syncControls();
    }

    handlePause() {
      if (this.pausingFromObserver) {
        this.syncControls();
        return;
      }
      if (this.isIntersecting && !this.player.ended) this.userPaused = true;
      this.syncControls();
    }

    handleEnded() {
      if (this.player && this.player.loop) return;
      this.playbackEnded = true;
      this.syncControls();
    }

    handleVolumeChange() {
      this.syncMuteButton();
    }

    isMuted() {
      if (!this.player) return true;
      return this.player.muted || this.player.volume === 0;
    }

    isPlaying() {
      if (!this.player) return false;
      return !this.player.paused && !this.player.ended;
    }

    toggleMute() {
      if (!this.player) return;
      const nextMuted = !this.isMuted();
      this.player.muted = nextMuted;
      if (!nextMuted && this.player.volume === 0) this.player.volume = 1;
      this.syncMuteButton();
    }

    togglePlayback() {
      if (!this.player) return;
      if (this.player.ended || this.playbackEnded) {
        try {
          this.player.currentTime = 0;
        } catch (e) {
          // ignore seek errors
        }
        this.playbackEnded = false;
        this.userPaused = false;
        this.tryPlay();
        this.syncControls();
        return;
      }
      if (this.player.paused) {
        this.userPaused = false;
        this.tryPlay();
      } else {
        this.userPaused = true;
        this.player.pause();
      }
      this.syncControls();
    }

    showCustomControls() {
      if (!this.controlsEl) return;
      if (this.source !== 'shopify') return;
      if (this.classList.contains('is-reduced-motion')) return;
      this.controlsEl.hidden = false;
      this.syncControls();
    }

    hideCustomControls() {
      if (!this.controlsEl) return;
      this.controlsEl.hidden = true;
    }

    syncControls() {
      this.syncMuteButton();
      this.syncToggleButton();
    }

    syncMuteButton() {
      if (!this.muteBtn) return;
      const muted = this.isMuted();
      this.muteBtn.setAttribute('aria-pressed', muted ? 'false' : 'true');
      this.muteBtn.setAttribute('aria-label', muted ? this.labelSoundOn : this.labelSoundOff);
      this.muteBtn.classList.toggle('is-muted', muted);
      const onIcon = this.muteBtn.querySelector('.iwt-video__icon--sound-on');
      const offIcon = this.muteBtn.querySelector('.iwt-video__icon--sound-off');
      if (onIcon) onIcon.hidden = muted;
      if (offIcon) offIcon.hidden = !muted;
    }

    syncToggleButton() {
      if (!this.toggleBtn) return;
      const playing = this.isPlaying();
      this.toggleBtn.setAttribute('aria-label', playing ? this.labelPause : this.labelPlay);
      this.toggleBtn.classList.toggle('is-playing', playing);
      const playIcon = this.toggleBtn.querySelector('.iwt-video__icon--play');
      const pauseIcon = this.toggleBtn.querySelector('.iwt-video__icon--pause');
      if (playIcon) playIcon.hidden = playing;
      if (pauseIcon) pauseIcon.hidden = !playing;
    }

    handleSectionUnload(event) {
      const section = this.closest('.shopify-section');
      if (section && event.target === section) {
        this.cleanup();
      }
    }

    cleanup() {
      this.disconnectObserver();
      this.detachMediaListeners();
      if (this.deferredObserver) {
        this.deferredObserver.disconnect();
        this.deferredObserver = null;
      }
      if (this.deferred && this.deferredClickBound) {
        this.deferred.removeEventListener('click', this.onDeferredClick);
        this.deferredClickBound = false;
      }
      if (this.player) {
        this.player.pause();
      }
      if (this.controlsEl && this.controlsBound) {
        this.controlsEl.removeEventListener('click', this.onControlsClick);
        this.controlsBound = false;
      }
      if (this.reducedPlay) {
        this.reducedPlay.removeEventListener('click', this.onReducedPlay);
        this.reducedPlayBound = false;
      }
      if (this.reducedQuery) {
        if (typeof this.reducedQuery.removeEventListener === 'function') {
          this.reducedQuery.removeEventListener('change', this.onReducedChange);
        } else if (typeof this.reducedQuery.removeListener === 'function') {
          this.reducedQuery.removeListener(this.onReducedChange);
        }
      }
      document.removeEventListener('shopify:section:unload', this.onSectionUnload);
      this._bound = false;
    }
  }

  customElements.define('iwt-video', IwtVideo);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('iwt-video').forEach((el) => {
      if (typeof el.connectedCallback === 'function' && !el._bound) {
        el.connectedCallback();
      }
    });
  });
})();
